import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import * as api from "../../lib/api.ts";
import { useSession, useViewer } from "../../lib/session.tsx";
import { VIEW_ONLY_TEXT, announceSessionChanged } from "../../lib/view.ts";

/** Сколько держится отметка об отказе записи. */
const NOTE_MS = 4000;

/**
 * Полоса «Просмотр кабинета: <имя>. Только чтение» (раздел 12.6
 * `docs/admin-panel-design.md`). Стоит над всеми маршрутами, в кабинете и в
 * комнате; высоту отдаёт переменной `--viewbar-h`, которую вычитают каркас и
 * комната (разбор у `.viewbar` в `styles.css`).
 *
 * Она же единственный получатель отказа `view_only`: пишущая кнопка под
 * просмотром отвечает отказом, и вместо шума на каждом экране полоса на
 * несколько секунд показывает одно общее «Только просмотр, изменения не
 * сохраняются». Повтор отказа отметку не множит, а продлевает.
 */
export function ViewBar() {
  const viewer = useViewer();
  const { state, signOut } = useSession();
  const navigate = useNavigate();
  const [noteAt, setNoteAt] = useState<number | null>(null);
  const viewing = viewer !== null;

  /**
   * Имя ПРЕПОДАВАТЕЛЯ, чей кабинет открыт: сессия и есть он, а `viewer` это
   * смотрящий админ.
   */
  const ownerName = state.status === "signed" ? state.me.displayName : "";

  useEffect(() => {
    if (!viewing) return;
    const root = document.documentElement;
    root.setAttribute("data-viewing", "");

    api.setViewOnlyHandler(() => setNoteAt(Date.now()));
    /**
     * Экраны, которые не ловят отказ сами, роняли бы его в консоль
     * необработанным промисом. Под просмотром это ожидаемый ответ, а не
     * поломка, поэтому именно его и только его здесь гасим.
     */
    const quiet = (e: PromiseRejectionEvent): void => {
      if (e.reason instanceof api.ApiError && e.reason.code === "view_only") e.preventDefault();
    };
    window.addEventListener("unhandledrejection", quiet);

    return () => {
      root.removeAttribute("data-viewing");
      api.setViewOnlyHandler(null);
      window.removeEventListener("unhandledrejection", quiet);
      setNoteAt(null);
    };
  }, [viewing]);

  useEffect(() => {
    if (noteAt === null) return;
    const timer = window.setTimeout(() => setNoteAt(null), NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [noteAt]);

  if (!viewing) return null;

  return (
    <div className="viewbar">
      <span className="viewbar__text">Просмотр кабинета: {ownerName}. Только чтение</span>
      <span className="viewbar__note" role="status">
        {noteAt !== null ? VIEW_ONLY_TEXT : ""}
      </span>
      <button
        type="button"
        className="btn btn--sm btn--quiet"
        onClick={() =>
          void signOut()
            // Вышли на этом устройстве в любом случае (`signOut` сам ставит anon)
            .catch(() => undefined)
            .then(() => {
              // Остальные вкладки просмотра в этом браузере тоже вышли: cookie одна
              announceSessionChanged(null);
              void navigate("/", { replace: true });
            })
        }
      >
        Выйти
      </button>
    </div>
  );
}
